import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { SA, SaCard, SaPageHeader } from "../../components/superadmin/saUi";
import {
  getPlatformSettings,
  PLATFORM_KEYS,
  PLATFORM_LABELS,
  PlatformKey,
  updatePlatformSettings,
} from "../../services/superadminService";
import { getErrorMessage } from "../../utils/apiResponse";

type TrialMode = "UNLIMITED" | "LOCKED" | "TRIAL";

interface TrialForm {
  mode: TrialMode;
  days: string;
}

const toTrialForm = (value: number | null): TrialForm => {
  if (value === null) return { mode: "UNLIMITED", days: "14" };
  if (value === 0) return { mode: "LOCKED", days: "14" };
  return { mode: "TRIAL", days: String(value) };
};

const fromTrialForm = (form: TrialForm): number | null => {
  if (form.mode === "UNLIMITED") return null;
  if (form.mode === "LOCKED") return 0;
  return Number(form.days);
};

const PlatformSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trial, setTrial] = useState<Record<PlatformKey, TrialForm>>({
    SPORTS: toTrialForm(null),
    CLINIC: toTrialForm(14),
  });
  const [salesNumber, setSalesNumber] = useState("");

  useEffect(() => {
    getPlatformSettings()
      .then((settings) => {
        setTrial({
          SPORTS: toTrialForm(settings.trialDays.SPORTS),
          CLINIC: toTrialForm(settings.trialDays.CLINIC),
        });
        setSalesNumber(settings.salesWhatsappNumber ?? "");
      })
      .catch(() => {
        // The interceptor already surfaced the message.
      })
      .finally(() => setLoading(false));
  }, []);

  const digits = salesNumber.replace(/\D/g, "");
  const salesValid = digits === "" || /^\d{6,15}$/.test(digits);
  const trialValid = PLATFORM_KEYS.every((key) => {
    const form = trial[key];
    if (form.mode !== "TRIAL") return true;
    const days = Number(form.days);
    return Number.isInteger(days) && days >= 1 && days <= 365;
  });

  const save = async () => {
    setSaving(true);
    try {
      const result = await updatePlatformSettings({
        trialDays: Object.fromEntries(
          PLATFORM_KEYS.map((key) => [key, fromTrialForm(trial[key])])
        ) as Record<PlatformKey, number | null>,
        salesWhatsappNumber: digits || null,
      });
      setTrial({
        SPORTS: toTrialForm(result.trialDays.SPORTS),
        CLINIC: toTrialForm(result.trialDays.CLINIC),
      });
      setSalesNumber(result.salesWhatsappNumber ?? "");
      toast.success("Settings saved.", { position: "top-right" });
    } catch (err: any) {
      toast.error(getErrorMessage(err, "Could not save the settings."), {
        position: "top-right",
      });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 820 }}>
      <SaPageHeader
        title="Settings"
        subtitle="Applies to new signups from now on. To change one organization, open it and use its Subscription section."
        icon={<SettingsOutlinedIcon />}
        tone="slate"
      />

      <SaCard
        title="Free trial for new signups"
        subtitle="What a new organization gets on each platform."
        icon={<HourglassTopOutlinedIcon />}
        tone="blue"
        flush
        sx={{ mb: 2.5 }}
      >
        {PLATFORM_KEYS.map((key, index) => (
          <Box key={key}>
            {index > 0 && <Divider />}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: { xs: 2, sm: 2.5 },
                py: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontWeight: 600, minWidth: 90, color: SA.text }}>
                {PLATFORM_LABELS[key]}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <TextField
                  select
                  size="small"
                  value={trial[key].mode}
                  onChange={(e) =>
                    setTrial((prev) => ({
                      ...prev,
                      [key]: { ...prev[key], mode: e.target.value as TrialMode },
                    }))
                  }
                  sx={{ minWidth: 230 }}
                >
                  <MenuItem value="TRIAL">Free trial</MenuItem>
                  <MenuItem value="UNLIMITED">No end date (unlimited)</MenuItem>
                  <MenuItem value="LOCKED">Locked until I activate them</MenuItem>
                </TextField>
                {trial[key].mode === "TRIAL" && (
                  <TextField
                    size="small"
                    type="number"
                    label="Days"
                    value={trial[key].days}
                    onChange={(e) =>
                      setTrial((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], days: e.target.value },
                      }))
                    }
                    slotProps={{ htmlInput: { min: 1, max: 365 } }}
                    sx={{ width: 110 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </SaCard>

      <SaCard
        title='"Contact us" WhatsApp number'
        subtitle="Owners whose subscription ended, or who ask for SMS or WhatsApp, tap Contact us and land in a WhatsApp chat with this number, with their request already typed."
        icon={<WhatsAppIcon />}
        tone="green"
        sx={{ mb: 3 }}
      >
        <TextField
          size="small"
          label="Number with country code"
          placeholder="96170123456"
          value={salesNumber}
          onChange={(e) => setSalesNumber(e.target.value)}
          error={!salesValid}
          helperText={
            salesValid ? "Digits only, no + or spaces." : "Use 6–15 digits, starting with the country code."
          }
          sx={{ minWidth: 260 }}
        />
        {digits && salesValid && (
          <Typography sx={{ fontSize: "0.85rem", mt: 1 }}>
            <Link href={`https://wa.me/${digits}`} target="_blank" rel="noopener noreferrer">
              Test the link
            </Link>
          </Typography>
        )}
      </SaCard>

      <Button
        variant="contained"
        disableElevation
        onClick={save}
        disabled={saving || !salesValid || !trialValid}
        sx={{ textTransform: "none", minHeight: 40, px: 3, borderRadius: "10px", fontWeight: 600 }}
      >
        {saving ? "Saving…" : "Save settings"}
      </Button>
    </Box>
  );
};

export default PlatformSettings;
